import { useState } from "react";
import { Loader2, Fuel, Receipt, Wrench, CalendarOff, ClipboardCheck, Send } from "lucide-react";
import toast from "react-hot-toast";
import { useDriverRequests, useDriverDashboard } from "../../hooks/useDriverPortal";

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 13.5, color: "#111827", outline: "none", fontFamily: "inherit",
};
const textareaStyle = { ...inputStyle, height: "auto", padding: "10px 12px", resize: "vertical" };
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };

const EXPENSE_CATEGORIES = { toll: "Toll", repair: "Repair", insurance_premium: "Insurance Premium", other: "Other" };
const INSPECTION_ITEMS = ["Tires", "Brakes", "Lights", "Horn", "Mirrors", "Wipers", "Fuel/Oil Level", "Engine", "Seatbelts", "Documents"];

const STATUS_STYLE = {
  pending:  { bg: "#FFFBEB", color: "#D97706", label: "Pending" },
  approved: { bg: "#F0FDF4", color: "#16A34A", label: "Approved" },
  rejected: { bg: "#FEF2F2", color: "#DC2626", label: "Rejected" },
};

const TYPE_META = {
  fuel:        { label: "Fuel Log",        Icon: Fuel },
  expense:     { label: "Expense",         Icon: Receipt },
  maintenance: { label: "Maintenance",     Icon: Wrench },
  leave:       { label: "Leave",           Icon: CalendarOff },
  inspection:  { label: "Pre-Trip Inspection", Icon: ClipboardCheck },
};

function summarizePayload(type, p) {
  if (type === "fuel") return `${p.liters ? `${p.liters}L` : ""}${p.cost ? ` · ₹${p.cost}` : ""}${p.fuelStation ? ` · ${p.fuelStation}` : ""}`.trim() || "Fuel fill-up";
  if (type === "expense") return `${EXPENSE_CATEGORIES[p.category] || p.category} · ₹${p.amount}`;
  if (type === "maintenance") return p.serviceType;
  if (type === "leave") return `${p.startDate} → ${p.endDate}`;
  if (type === "inspection") {
    const flagged = (p.checklist || []).filter(c => c.status !== "ok");
    return flagged.length ? `${flagged.length} issue(s) flagged` : "All clear";
  }
  return "";
}

function FormShell({ onSubmit, submitting, children }) {
  return (
    <form onSubmit={onSubmit} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
      {children}
      <button type="submit" disabled={submitting} style={{ height: 40, borderRadius: 9, border: "none", background: submitting ? "#9CA3AF" : "#4F46E5", color: "#fff", fontWeight: 700, fontSize: 13.5, cursor: submitting ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <Send size={13} /> {submitting ? "Submitting…" : "Submit Request"}
      </button>
    </form>
  );
}

export default function DriverRequests() {
  const { data: dashboard } = useDriverDashboard();
  const { requests, loading, submit } = useDriverRequests();
  const [activeType, setActiveType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const perms = dashboard?.permissions || {};
  const allowedTypes = [
    perms.allowFuelEntry !== false && "fuel",
    perms.allowExpenseEntry !== false && "expense",
    perms.allowMaintenanceRequests !== false && "maintenance",
    perms.allowLeaveRequests !== false && "leave",
    perms.allowPreTripInspection !== false && "inspection",
  ].filter(Boolean);

  async function handleSubmit(type, payload) {
    setSubmitting(true);
    try {
      const res = await submit(type, payload);
      toast.success(res.status === "pending" ? "Submitted — awaiting owner approval" : "Submitted and auto-cleared");
      setActiveType(null);
    } catch (err) {
      toast.error(err.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 2 }}>Requests</div>
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>Submit fuel logs, expenses, maintenance issues, leave, and pre-trip inspections for your owner's approval</div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {allowedTypes.map(type => {
            const { label, Icon } = TYPE_META[type];
            const active = activeType === type;
            return (
              <button
                key={type}
                onClick={() => setActiveType(active ? null : type)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 9,
                  border: active ? "1.5px solid #4F46E5" : "1px solid #E5E7EB",
                  background: active ? "#EEF2FF" : "#fff", color: active ? "#4F46E5" : "#374151",
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              ><Icon size={14} /> {label}</button>
            );
          })}
        </div>

        {activeType === "fuel" && <FuelForm onSubmit={p => handleSubmit("fuel", p)} submitting={submitting} />}
        {activeType === "expense" && <ExpenseForm onSubmit={p => handleSubmit("expense", p)} submitting={submitting} />}
        {activeType === "maintenance" && <MaintenanceForm onSubmit={p => handleSubmit("maintenance", p)} submitting={submitting} />}
        {activeType === "leave" && <LeaveForm onSubmit={p => handleSubmit("leave", p)} submitting={submitting} />}
        {activeType === "inspection" && <InspectionForm onSubmit={p => handleSubmit("inspection", p)} submitting={submitting} />}

        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginTop: 28, marginBottom: 10 }}>My Requests</div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 24 }}><Loader2 size={16} style={{ animation: "spin 1s linear infinite", color: "#9CA3AF" }} /></div>
        ) : !requests.length ? (
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: 20, textAlign: "center", fontSize: 13, color: "#9CA3AF" }}>No requests submitted yet</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {requests.map(r => {
              const st = STATUS_STYLE[r.status];
              const { label, Icon } = TYPE_META[r.type];
              return (
                <div key={r.id} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Icon size={14} color="#6B7280" />
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{label}</span>
                    </div>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 5, padding: "2px 8px" }}>{st.label}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 4 }}>{summarizePayload(r.type, r.payload)}</div>
                  {r.status === "rejected" && r.reviewerNote && (
                    <div style={{ fontSize: 12, color: "#DC2626", marginTop: 6, background: "#FEF2F2", borderRadius: 6, padding: "6px 10px" }}>Reason: {r.reviewerNote}</div>
                  )}
                  {r.status === "approved" && r.reviewerNote && (
                    <div style={{ fontSize: 12, color: "#16A34A", marginTop: 6, background: "#F0FDF4", borderRadius: 6, padding: "6px 10px" }}>Note: {r.reviewerNote}</div>
                  )}
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>{new Date(r.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}

function FuelForm({ onSubmit, submitting }) {
  const [filledAt, setFilledAt] = useState(new Date().toISOString().slice(0, 16));
  const [odometerKm, setOdometerKm] = useState("");
  const [liters, setLiters] = useState("");
  const [cost, setCost] = useState("");
  const [fuelStation, setFuelStation] = useState("");
  return (
    <FormShell submitting={submitting} onSubmit={e => { e.preventDefault(); onSubmit({ filledAt: new Date(filledAt).toISOString(), odometerKm: odometerKm ? Number(odometerKm) : null, liters: liters ? Number(liters) : null, cost: cost ? Number(cost) : null, fuelStation: fuelStation || null }); }}>
      <div><label style={labelStyle}>Filled At</label><input type="datetime-local" style={inputStyle} value={filledAt} onChange={e => setFilledAt(e.target.value)} required /></div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}><label style={labelStyle}>Odometer (km)</label><input type="number" style={inputStyle} value={odometerKm} onChange={e => setOdometerKm(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={labelStyle}>Liters</label><input type="number" step="0.01" style={inputStyle} value={liters} onChange={e => setLiters(e.target.value)} /></div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}><label style={labelStyle}>Cost (₹)</label><input type="number" step="0.01" style={inputStyle} value={cost} onChange={e => setCost(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={labelStyle}>Fuel Station</label><input style={inputStyle} value={fuelStation} onChange={e => setFuelStation(e.target.value)} /></div>
      </div>
    </FormShell>
  );
}

function ExpenseForm({ onSubmit, submitting }) {
  const [category, setCategory] = useState("toll");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  return (
    <FormShell submitting={submitting} onSubmit={e => { e.preventDefault(); onSubmit({ category, amount: Number(amount), expenseDate, notes: notes || null }); }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Category</label>
          <select style={{ ...inputStyle, cursor: "pointer" }} value={category} onChange={e => setCategory(e.target.value)}>
            {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={labelStyle}>Amount (₹)</label><input type="number" step="0.01" style={inputStyle} value={amount} onChange={e => setAmount(e.target.value)} required /></div>
      </div>
      <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={expenseDate} onChange={e => setExpenseDate(e.target.value)} required /></div>
      <div><label style={labelStyle}>Notes</label><textarea rows={2} style={textareaStyle} value={notes} onChange={e => setNotes(e.target.value)} /></div>
    </FormShell>
  );
}

function MaintenanceForm({ onSubmit, submitting }) {
  const [serviceType, setServiceType] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueOdometerKm, setDueOdometerKm] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <FormShell submitting={submitting} onSubmit={e => { e.preventDefault(); onSubmit({ serviceType, dueDate: dueDate || null, dueOdometerKm: dueOdometerKm ? Number(dueOdometerKm) : null, notes: notes || null }); }}>
      <div><label style={labelStyle}>Issue / Service Needed</label><input style={inputStyle} value={serviceType} onChange={e => setServiceType(e.target.value)} placeholder="e.g. Brake pads worn" required /></div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}><label style={labelStyle}>Due Date (optional)</label><input type="date" style={inputStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={labelStyle}>Due Odometer (km)</label><input type="number" style={inputStyle} value={dueOdometerKm} onChange={e => setDueOdometerKm(e.target.value)} /></div>
      </div>
      <div><label style={labelStyle}>Notes</label><textarea rows={2} style={textareaStyle} value={notes} onChange={e => setNotes(e.target.value)} /></div>
    </FormShell>
  );
}

function LeaveForm({ onSubmit, submitting }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  return (
    <FormShell submitting={submitting} onSubmit={e => { e.preventDefault(); onSubmit({ startDate, endDate, reason: reason || null }); }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}><label style={labelStyle}>Start Date</label><input type="date" style={inputStyle} value={startDate} onChange={e => setStartDate(e.target.value)} required /></div>
        <div style={{ flex: 1 }}><label style={labelStyle}>End Date</label><input type="date" style={inputStyle} value={endDate} onChange={e => setEndDate(e.target.value)} required /></div>
      </div>
      <div><label style={labelStyle}>Reason</label><textarea rows={2} style={textareaStyle} value={reason} onChange={e => setReason(e.target.value)} /></div>
    </FormShell>
  );
}

function InspectionForm({ onSubmit, submitting }) {
  const [items, setItems] = useState(() => Object.fromEntries(INSPECTION_ITEMS.map(i => [i, { status: "ok", note: "" }])));
  const [odometerKm, setOdometerKm] = useState("");
  const [notes, setNotes] = useState("");

  function toggle(item) {
    setItems(prev => ({ ...prev, [item]: { ...prev[item], status: prev[item].status === "ok" ? "issue" : "ok" } }));
  }
  function setNote(item, note) {
    setItems(prev => ({ ...prev, [item]: { ...prev[item], note } }));
  }

  return (
    <FormShell
      submitting={submitting}
      onSubmit={e => {
        e.preventDefault();
        const checklist = INSPECTION_ITEMS.map(i => ({ item: i, status: items[i].status, note: items[i].note || null }));
        onSubmit({ odometerKm: odometerKm ? Number(odometerKm) : null, checklist, notes: notes || null });
      }}
    >
      <div><label style={labelStyle}>Odometer (km)</label><input type="number" style={inputStyle} value={odometerKm} onChange={e => setOdometerKm(e.target.value)} /></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {INSPECTION_ITEMS.map(item => {
          const ok = items[item].status === "ok";
          return (
            <div key={item}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: ok ? "#F9FAFB" : "#FEF2F2", borderRadius: 8, border: `1px solid ${ok ? "#E5E7EB" : "#FECACA"}` }}>
                <span style={{ fontSize: 13, color: "#111827" }}>{item}</span>
                <button type="button" onClick={() => toggle(item)} style={{ fontSize: 11.5, fontWeight: 700, border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", background: ok ? "#F0FDF4" : "#FEE2E2", color: ok ? "#16A34A" : "#DC2626" }}>
                  {ok ? "OK" : "Issue"}
                </button>
              </div>
              {!ok && (
                <input
                  style={{ ...inputStyle, height: 32, marginTop: 4, fontSize: 12.5 }}
                  placeholder={`Describe the ${item.toLowerCase()} issue…`}
                  value={items[item].note}
                  onChange={e => setNote(item, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
      <div><label style={labelStyle}>Additional Notes</label><textarea rows={2} style={textareaStyle} value={notes} onChange={e => setNotes(e.target.value)} /></div>
    </FormShell>
  );
}
